// One agent request, under its own title and who asked (BL-189; pages/ux-full inbox/approval), as To approve
// draws it, for every page that shows the requests: Inbox › To approve (one request, its input open), and
// Accounting › Agent requests and Inbox › Agents and approvals › Approvals (a row each, `fold`: its input
// in a row that opens in place, so the list stays scannable). Top to bottom: the flag when the run found
// instructions in customer text, why, exactly what will run (KeyValue, the exact value on hover) and the
// invoices it names, each a link; what approving a money request also needs; then the decision: Approve &
// run (or, on your own AI tool's request, letting it act as you for a while), or Decline with a note the
// agent reads next time, and when it lapses. Decided, a done Notice says how, and how its run went. One
// that can't be decided says why in place of the decision; when only its agent is stopped it can still be
// declined, which runs nothing. Each page decides through its own onDecide. request-body.css spaces the
// lines, tokens only.
import type { AgentRequestProps } from '@flowology/ui-web';
import { Button, KeyValue, Label, Link, Notice, RecordRow, TextField } from '@flowology/ui-web/fl';
import { Fragment, useState } from 'react';
import { decidedText, type Decision } from '../inbox/inbox-pages-model.ts';
import { docHref } from './doc-link.ts';
import './request-body.css';

const EXACT = 'Exactly what will run (it can’t be changed)';

export interface RequestBodyProps {
  r: AgentRequestProps;
  busy: boolean;
  onDecide: (d: Decision) => void;
  /** In a list: what will run folds in a row that opens in place. Left out, it shows open. */
  fold?: boolean;
  /** The invoices its input names, by number (QA-267), each a link to its document. */
  invoices?: readonly { id: string; label: string }[];
  /** The server lets this person decide it, though `r.cannotDecide` says why it can't run now (its agent
   * is stopped): it can still be declined. */
  mayDecline?: boolean;
}

/** Exactly what will run: its facts in words, with the exact value on hover, and the invoices it names. */
function Exact({ r, invoices = [] }: Pick<RequestBodyProps, 'r' | 'invoices'>) {
  return (
    <>
      <KeyValue
        items={(r.rows ?? []).map((x) => ({
          label: x.label,
          value: x.exact && x.exact !== x.text ? <span title={x.exact}>{x.text}</span> : x.text,
        }))}
      />
      {invoices.length > 0 && (
        <p className="rq-line rq-muted">
          Invoices in it:{' '}
          {invoices.map((inv, i) => (
            <Fragment key={inv.id}>
              {i > 0 && ', '}
              <Link href={docHref({ kind: 'invoice', id: inv.id })}>{inv.label}</Link>
            </Fragment>
          ))}
        </p>
      )}
    </>
  );
}

export function RequestBody({
  r,
  busy,
  onDecide,
  fold = false,
  invoices,
  mayDecline = false,
}: RequestBodyProps) {
  const [declining, setDeclining] = useState(false);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const waiting = r.state === 'pending';
  const decline = (
    <Link isDisabled={busy} onPress={() => setDeclining(true)}>
      Decline…
    </Link>
  );
  return (
    <div className="rq">
      {r.flagged && (
        <p className="rq-line">
          <Label kind="bad">Instructions found in customer text</Label>
        </p>
      )}
      {r.explanation && (
        <p className="rq-line">
          <span className="rq-muted">Why: </span>
          {r.explanation}
        </p>
      )}
      {!r.rows ? (
        <p className="rq-lead">The details show only to people who could do this themselves.</p>
      ) : fold ? (
        <div className="rq-fold">
          <RecordRow
            id={`rq-${r.id}-exact`}
            title={EXACT}
            label={EXACT}
            isOpen={open}
            onToggle={() => setOpen((o) => !o)}
            expanded={<Exact r={r} {...(invoices ? { invoices } : {})} />}
          />
        </div>
      ) : (
        <>
          <p className="rq-lead">{EXACT}</p>
          <Exact r={r} {...(invoices ? { invoices } : {})} />
        </>
      )}
      {r.money && waiting && (
        <p className="rq-money">
          Money: approving also needs the owner’s grant for {r.money.toLowerCase()}s.
        </p>
      )}
      {!waiting && (
        <div className="rq-said">
          <Notice tone="done" title={decidedText(r)}>
            {r.decided?.note ? `Note to the agent: “${r.decided.note}”` : null}
          </Notice>
        </div>
      )}
      {waiting &&
        (declining ? (
          <div className="rq-decline">
            <TextField
              label="Note for the agent (it reads this next time)"
              value={note}
              maxLength={500}
              onChange={setNote}
            />
            <div className="rq-acts">
              <Button
                variant="primary"
                isBusy={busy}
                onPress={() =>
                  onDecide({ decision: 'decline', ...(note.trim() ? { note: note.trim() } : {}) })
                }
              >
                {busy ? 'Declining…' : 'Decline'}
              </Button>
              <Link isDisabled={busy} onPress={() => setDeclining(false)}>
                Back
              </Link>
            </div>
          </div>
        ) : r.cannotDecide ? (
          <>
            <p className="rq-waits">{r.cannotDecide}</p>
            {/* only its agent is stopped: declining runs nothing, so it stays open */}
            {mayDecline && <div className="rq-acts">{decline}</div>}
          </>
        ) : (
          <>
            {/* the one primary: "act as me" where it's offered (the prototype's recommended way), else Approve */}
            <div className="rq-acts">
              {r.canTrust && (
                <Button
                  variant="primary"
                  isBusy={busy}
                  onPress={() => onDecide({ decision: 'approve', trust: true })}
                >
                  Approve, and let it act as me for {r.trustHours} hours
                </Button>
              )}
              {r.canTrust ? (
                <Link isDisabled={busy} onPress={() => onDecide({ decision: 'approve' })}>
                  {busy ? 'Approving…' : 'Approve & run'}
                </Link>
              ) : (
                <Button variant="primary" isBusy={busy} onPress={() => onDecide({ decision: 'approve' })}>
                  {busy ? 'Approving…' : 'Approve & run'}
                </Button>
              )}
              {decline}
            </div>
            {r.expires && <p className="rq-sub">It lapses {r.expires} if nobody decides.</p>}
          </>
        ))}
    </div>
  );
}
